import * as swcHelpers from '@swc/helpers';
import { ApolloLink, HttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import ApolloLinkTimeout from 'apollo-link-timeout';

// TODO: fix/improve token caching
// This is a hardcoded map of the queries and mutations that require authentication, when this
// map isn't in place, every single graphQL request will also send a request to
// check the session of auth0, this is a naive way to skip over all the graphQL
// queries which we know don't require authentication
const authOperations = new Set([
    'CancelSubscription',
    'ChangeSubscription',
    'CheckCustomerConsents',
    'CreateEyeExam',
    'CreateStoredCardPayment',
    'CreateStoredSepaPaymentMethod',
    'findSubscription',
    'getSuspendSubscription',
    'getBillingAddress',
    'getChangeSubscriptionPreview',
    'getCurrentSubscriptionDetails',
    'getOptIns',
    'getPersonalDetails',
    'getSocialIdentity',
    'getPreferredStore',
    'GetPreferredStore',
    'GetPreferredStoreWithInventory',
    'getPreferredStoreCode',
    'GetPreferredStoreCode',
    'getRecentOrders',
    'getPrescriptionDownloads',
    'getAppointmentById',
    'getMyAppointments',
    'getMySubscriptions',
    'getOrderDetails',
    'getMyOrderHistory',
    'getSubscriptionDeliveries',
    'getSubscriptionPayments',
    'getSubscriptionPrescription',
    'getSubscriptionPrescriptionProducts',
    'getSubscriptionNotifications',
    'getSubscriptionProductRateplans',
    'getSuspensionResumeDates',
    'previewChangeSubscription',
    'ResumeSubscription',
    'RetrySubscriptionPayment',
    'SaveNewConsent',
    'SuspendSubscription',
    'UpdateBillingAddress',
    'UpdateDeliveryAddress',
    'updateOptIns',
    'updatePersonalDetails',
    'UpdatePersonalDetails',
    'UpdateSubscriptionBillingAddress',
    'UpdateSubscriptionPrescription',
    'storedPaymentMethods',
    'ChangePaymentMethod',
    'completeChallengeFlowStoredPayment',
    'activateSocialAccount',
    'requestSocialLoginMerge',
    'getBookAppointmentUser',
    'MergeShoppinglists',
    'GetMyWishlist',
    'GetMyWishlistId',
    'GetMySubscriptions',
    'GetMySubscriptionDetails',
    'UpdateSubscriptionInterval',
    'UpdateSubscriptionBillingAddress',
    'UpdateSubscriptionDeliveryAddress',
    'PauseSubscription', 
]);
/**
 * This set is for operations that only require the auth token when the customer is logged in. In some specific use case `authOperations` is not sufficient enough.
 * For instance, for the `SubscribeNewsletter` operation, the BE requires the token to do some additional logic for logged-in customers.
 * Instead of having two separate mutations, we created one mutation where both logged-in and logged-out customers subscribe their email address.
 * Via this extension the FE only sends a token when the customer is logged-in.
 */ const operationsWithOptionalToken = new Set([
    'SubscribeNewsletter',
    'PlaceOrder',
    'UpsertShoppingList',
    'getShoppingListById',
    'GetShoppingListCount',
    'AddToShoppinglist',
    'RemoveFromShoppinglist',
    'CartExpressPaymentMethods', 
]);
const authCallback = (options)=>(operation, previousRequest)=>new Promise(function() {
            var _ref = swcHelpers.asyncToGenerator(function*(resolve, reject) {
                // Only continue if the operationName is one that we know requires authentication
                const requiresAuth = operation.operationName && (authOperations.has(operation.operationName) || operationsWithOptionalToken.has(operation.operationName));
                if (!requiresAuth || !options.getToken) {
                    return resolve(previousRequest);
                }
                try {
                    const token = yield options.getToken();
                    resolve({
                        headers: swcHelpers.objectSpread({}, previousRequest.headers, {
                            authorization: `Bearer ${token}`
                        })
                    });
                } catch (e) {
                    if (operation.operationName && operationsWithOptionalToken.has(operation.operationName)) {
                        return resolve(previousRequest);
                    }
                    reject(e);
                }
            });
            return function(resolve, reject) {
                return _ref.apply(this, arguments);
            };
        }())
;
const authLink = (options)=>setContext(authCallback(options))
;
const httpLink = (options)=>new HttpLink(swcHelpers.objectSpread({
        credentials: 'same-origin',
        fetch
    }, options))
;
const errorLink = onError(({ graphQLErrors , networkError  })=>{
    if (graphQLErrors) {
        graphQLErrors.forEach((error)=>console.error(`[GraphQL error]: ${error.message}`)
        );
    }
    if (networkError) {
        console.error('[Network error]:', networkError);
    }
});
const forwardHeaders = (operation, forward)=>{
    const context = operation.getContext();
    const forwardHeaders1 = context.forwardHeaders;
    operation.setContext(({ headers ={}  })=>({
            headers: swcHelpers.objectSpread({}, headers, forwardHeaders1 && forwardHeaders1)
        })
    );
    return forward(operation);
};
const forwardHeadersLink = new ApolloLink(forwardHeaders);
const timeoutLink = (timeoutMs, statusCode)=>new ApolloLinkTimeout(timeoutMs, statusCode)
;

export { authCallback, authLink, errorLink, forwardHeaders, forwardHeadersLink, httpLink, timeoutLink };
//# sourceMappingURL=links.js.map
